/* global LPBaseConfig, LPLocalizationSettings, jQuery */
/**
 * Landing Page Custom Handlers v3
 *
 * Class-based, framework-agnostic rewrite of lp-custom-handlers-v2.js.
 * Drop-in replacement: just include this script in <head>.
 * jQuery is only used for custom event dispatching/listening (when available)
 * to stay compatible with external jQuery event consumers.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Static Helpers (pure functions — no instance state)
  // ---------------------------------------------------------------------------

  /**
   * Deep-merge source into a shallow copy of target (recursive).
   * Replaces jQuery's $.extend(true, {}, target, source).
   */
  function deepMerge(target, source) {
    const output = Object.assign({}, target);

    if (isPlainObject(target) && isPlainObject(source)) {
      Object.keys(source).forEach((key) => {
        if (isPlainObject(source[key])) {
          output[key] = key in target ? deepMerge(target[key], source[key]) : source[key];
        } else {
          output[key] = source[key];
        }
      });
    }

    return output;
  }

  function isPlainObject(val) {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }

  /** querySelector shorthand */
  const qs = (sel, root = document) => root.querySelector(sel);

  /** querySelectorAll shorthand — returns a real Array */
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

  /**
   * Check whether an element is currently visible within the viewport.
   * Replaces the jQuery .isInViewport() plugin.
   */
  function isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  }

  /**
   * Format a price stored in cents to a rounded, locale-formatted dollar string.
   * Example: 2999 → "30", 149900 → "1,499"
   * Extracted from the repeated pattern: Math.round(value.toFixed(2) / 100).toLocaleString('en-US')
   */
  function formatCentsToDisplay(cents) {
    return Math.round(Number(cents).toFixed(2) / 100).toLocaleString('en-US');
  }

  // ---------------------------------------------------------------------------
  // Number-to-words utility
  // ---------------------------------------------------------------------------

  /**
   * Convert a positive integer (0–9999) to its English word representation.
   */
  function humanize(num) {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    if (num < 0) throw new Error('Negative numbers are not supported.');
    if (num === 0) return 'zero';
    if (num < 20) return ones[num];

    const s = num.toString();

    if (s.length === 2) {
      return tens[s[0]] + ' ' + ones[s[1]];
    }
    if (s.length === 3) {
      const tail = +(s[1] + s[2]);
      return ones[s[0]] + ' hundred' + (tail === 0 ? '' : ' and ' + humanize(tail));
    }
    if (s.length === 4) {
      const tail = +(s[1] + s[2] + s[3]);
      if (tail === 0) return ones[s[0]] + ' thousand';
      if (tail < 100) return ones[s[0]] + ' thousand and ' + humanize(tail);
      return ones[s[0]] + ' thousand ' + humanize(tail);
    }
  }

  // ---------------------------------------------------------------------------
  // parseProductPrice helpers
  // ---------------------------------------------------------------------------

  /**
   * Detect and strip leading negative indicators: parentheses "(123)" or "-123".
   * Returns { isNegative: boolean, cleaned: string }.
   */
  function detectNegativeSign(str) {
    if (/^\(.*\)$/.test(str)) {
      return { isNegative: true, cleaned: str.slice(1, -1) };
    }
    if (/^\s*-\s*/.test(str)) {
      return { isNegative: true, cleaned: str.replace(/^\s*-\s*/, '') };
    }
    return { isNegative: false, cleaned: str };
  }

  /**
   * Normalize ambiguous decimal/thousands separators (dot and comma)
   * into a standard parseFloat-ready string.
   */
  function normalizeDecimalSeparators(str) {
    const hasDot = str.indexOf('.') !== -1;
    const hasComma = str.indexOf(',') !== -1;

    if (hasDot && hasComma) {
      // Both present — rightmost separator is the decimal
      const lastDot = str.lastIndexOf('.');
      const lastComma = str.lastIndexOf(',');
      const decimalSep = lastComma > lastDot ? ',' : '.';
      const thousandsSep = decimalSep === ',' ? '.' : ',';
      const re = new RegExp('\\' + thousandsSep, 'g');
      return str.replace(re, '').replace(decimalSep, '.');
    }

    if (hasComma && !hasDot) {
      const parts = str.split(',');
      const right = parts[parts.length - 1];
      // 3-digit right group → thousands separator; otherwise → decimal
      return right.length === 3 ? str.replace(/,/g, '') : str.replace(/,/g, '.');
    }

    if (hasDot && !hasComma) {
      const parts = str.split('.');
      const right = parts[parts.length - 1];
      // 3-digit right group → thousands separator; otherwise → decimal
      return right.length === 3 ? str.replace(/\./g, '') : str;
    }

    return str; // no separators
  }

  /**
   * Parse a human-readable price string into a rounded, locale-formatted integer string.
   * Handles currency symbols, negative indicators, varied decimal/thousands separators.
   * Returns the original string unchanged for non-numeric values like "Free" or "N/A".
   */
  function parseProductPrice(price) {
    if (typeof price !== 'string') return price;

    const original = price.trim();
    if (!original) return original;

    const lower = original.toLowerCase();
    if (lower === 'free' || lower === 'included' || lower === 'n/a') return original;

    // Detect negatives
    const { isNegative, cleaned } = detectNegativeSign(original);
    let s = cleaned;

    // Normalize NBSP → space, trim, strip currency symbols/letters
    s = s.replace(/\u00A0/g, ' ').trim();
    s = s.replace(/[^0-9.,\-()]/g, '');
    s = s.replace(/\s+/g, '');
    s = s.replace(/^[^0-9-]+/, ''); // strip leading non-digit chars

    if (!s) return original;

    let normalized = normalizeDecimalSeparators(s);

    // Final cleanup — keep only digits, dot, minus
    normalized = normalized.replace(/[^\d.-]/g, '');

    const parsed = parseFloat(normalized);
    if (isNaN(parsed)) return original;

    const value = isNegative ? -parsed : parsed;

    return Math.round(value).toLocaleString('en-US');
  }

  // ---------------------------------------------------------------------------
  // jQuery-compatible event helpers
  // ---------------------------------------------------------------------------

  /**
   * Dispatch a custom event on document.
   * Uses jQuery when available so that external $(document).on() listeners fire.
   * Falls back to native CustomEvent when jQuery is absent.
   */
  function trigger(name, detail) {
    if (typeof jQuery !== 'undefined') {
      jQuery(document).trigger(name, detail);
    } else {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }
  }

  /**
   * Listen for a custom event on document.
   * Uses jQuery when available so that external $(document).trigger() calls are caught.
   * The handler signature is always (event, data) — matching jQuery convention.
   */
  function listen(name, handler) {
    if (typeof jQuery !== 'undefined') {
      jQuery(document).on(name, handler);
    } else {
      // Adapt native event to match jQuery's (event, data) signature
      document.addEventListener(name, (e) => handler(e, e.detail));
    }
  }

  // ---------------------------------------------------------------------------
  // LPCustomHandlers Class
  // ---------------------------------------------------------------------------

  class LPCustomHandlers {
    /**
     * @param {Object} baseConfig - The global LPBaseConfig object
     */
    constructor(baseConfig = {}) {
      // Merge base config with local defaults
      this.config = deepMerge({
        dev: false,
        updatableClasses: {
          currencySymbol: '.updatable--currency-symbol',
          totalPrice: '.updatable--total-price',
          subtotalPrice: '.updatable--subtotal-price',
          totalPriceWithExtra: '.updatable--total-price-with-extra',
          subtotalPriceWithExtra: '.updatable--subtotal-price-with-extra',
          extraProductsSubTotalPrice: '.updatable--extra-products-subtotal-price',
          totalSavingsWithExtraProducts: '.updatable--total-savings-with-extra-products',
          quantity: '.updatable--quantity',
          quantityWords: '.updatable--quantity-words',
          quantityField: '.updatable--quantity-field',
          selectableBundleBlock: '.selectable--bundle-block',
          klarnaContainer: '.updatable--klarna-container',
          klarnaPlacement: '.updatable--klarna-placement',
          checkoutButton: '.updatable--checkout-button',
          minusButton: '.updatable--qtty-minus-button',
          plusButton: '.updatable--qtty-plus-button',
          hasDiscount: '.updatable--hasDiscount',
          discountPercent: '.updatable--discountPercent',
          discountedQuantity: '.updatable--discounted-quantity',
          totalDiscount: '.updatable--total-discount',
          singleItemPrice: '.updatable--singleItemPrice',
          discountTable: '.updatable--discountTable',
          giftBlocks: '.updatable--giftBlocks',
          qttyImages: '.updatable--qtty-images',
        },
        observableClasses: {
          ajaxForm: '.observable--ajaxForm',
          buyNowForm: '.observable--buy-now-form',
          addToCartForm: '.observable--add-to-cart-form',
          mainProductDescription: '.observable--main-product-description',
          fixedAddToCart: '.observable--fixed-add-to-cart',
          qttyChangeButton: '.observable--qtty-change',
          extraProducts: '.observable--extra-products',
        },
        geSettings: {
          timeToWait: 2000,
          isOperated: false,
        },
        drawerCart: null,
      }, baseConfig);

      // Create dev logger
      this.log = this._createLogger();

      // Backward compat: expose config globally
      window.LPConfig = this.config;

      // Bind document-level custom events (LP:update-qtty, etc.)
      this._bindCustomEvents();

      // DOM-ready: bind UI event listeners and run initialization
      this._onReady(() => this._init());

      this.log.info('LPCustomHandlers constructed', this.config);
    }

    // -----------------------------------------------------------------------
    // Logger
    // -----------------------------------------------------------------------

    /**
     * Create a dev-gated logger. Checks this.config.dev dynamically so
     * logging can be toggled at runtime (e.g. from devtools).
     */
    _createLogger() {
      return {
        info: (...args) => this.config.dev && console.log('[LP]', ...args),
        warn: (...args) => this.config.dev && console.warn('[LP]', ...args),
        error: (...args) => this.config.dev && console.error('[LP]', ...args),
        table: (data) => this.config.dev && console.table(data),
      };
    }

    // -----------------------------------------------------------------------
    // Pricing — Discount resolution
    // -----------------------------------------------------------------------

    /**
     * Determine the discount tier that applies for the given quantity.
     * Returns { qtty, percent, singleItemPrice, multiplier }.
     */
    checkDiscount(qtty, regularPricing) {
      if (!this.config.lpVariant) {
        this.log.warn('checkDiscount: lpVariant not configured');
        return { qtty: 1, percent: 0, singleItemPrice: 0, multiplier: 1 };
      }

      const noDiscount = {
        qtty: 1,
        percent: 0,
        singleItemPrice: this.config.lpVariant.price,
        multiplier: 1,
      };

      if (regularPricing) return noDiscount;

      let currentDiscount = noDiscount;

      if (qtty >= this.config.minQttyToApplyDiscounts) {
        for (let i = 0; i < this.config.discounts.length; i++) {
          if (qtty >= this.config.discounts[i].qtty) {
            currentDiscount = this.config.discounts[i];
            currentDiscount.multiplier = (1 - this.config.discounts[i].percent / 100).toFixed(2);
          }
        }
      }

      this.log.info('checkDiscount', { qtty, regularPricing, currentDiscount });
      return currentDiscount;
    }

    // -----------------------------------------------------------------------
    // Pricing — Extra products
    // -----------------------------------------------------------------------

    /**
     * Sum prices of checked extra-product checkboxes (unique per product ID).
     */
    getCheckedExtrasPrice() {
      const price = { subTotal: 0, total: 0 };
      const seen = [];

      qsa(this.config.observableClasses.extraProducts).forEach((el) => {
        const productId = el.dataset.productId;
        if (el.checked && !seen.includes(productId)) {
          const p = parseInt(el.dataset.price);
          price.subTotal += p;
          price.total += p;
          seen.push(productId);
        }
      });

      return price;
    }

    /**
     * Sum prices for free-gift tiers based on quantity.
     * Gift total is 0 (free), but subTotal reflects the retail value.
     */
    getGiftProductsPrice(qtty) {
      const price = { subTotal: 0, total: 0 };

      if (!this.config.giftProducts?.length) return price;

      this.config.giftProducts.forEach((gift) => {
        const selectedTier = gift.tiers.find((tier) => tier.qtty <= qtty);
        if (selectedTier) {
          price.subTotal += parseInt(gift.variant_price) * selectedTier.gifted;
          // total stays 0 — it's a free gift
        }
      });

      return price;
    }

    /**
     * Combine checked extras + gift products into a single price object.
     */
    getExtraProductsPrice(qtty) {
      const extras = this.getCheckedExtrasPrice();
      const gifts = this.getGiftProductsPrice(qtty);

      const combined = {
        subTotal: extras.subTotal + gifts.subTotal,
        total: extras.total + gifts.total,
      };

      this.log.info('getExtraProductsPrice', combined);
      return combined;
    }

    // -----------------------------------------------------------------------
    // Pricing — Main price calculation
    // -----------------------------------------------------------------------

    /**
     * Core arithmetic: compute raw prices from quantity and discount.
     */
    calculateBasePrice(qtty, discount) {
      const variantPrice = this.config.lpVariant?.price || 0;
      const subTotal = variantPrice * qtty;
      const total = Math.round(subTotal * discount.multiplier);
      const singleItemDiscount = Math.round(variantPrice - discount.singleItemPrice);

      return { subTotal, total, singleItemDiscount };
    }

    /**
     * Build the full display-ready price object from base prices and extra products.
     */
    buildPriceDisplay(base, extras, discount) {
      const usdSubTotalWithExtraProducts = base.subTotal + extras.subTotal;
      const usdTotalWithExtraProducts = base.total + extras.total;
      const usdTotalDiscount = base.subTotal - base.total;
      const totalSavingsWithExtraProducts = usdSubTotalWithExtraProducts - usdTotalWithExtraProducts;

      return {
        appliedDiscount: discount,
        usdSubTotal: base.subTotal,
        usdTotal: base.total,
        usdTotalDiscount,
        subTotalPrice: base.subTotal,
        totalPrice: base.total,
        extraProductsPrice: extras,
        usdSubTotalWithExtraProducts,
        usdTotalWithExtraProducts,
        singleItemDiscount: base.singleItemDiscount,
        displaySubtotalPrice: formatCentsToDisplay(base.subTotal),
        displayTotalPrice: formatCentsToDisplay(base.total),
        displaySingleItemPrice: formatCentsToDisplay(discount.singleItemPrice),
        displaySingleItemDiscount: formatCentsToDisplay(base.singleItemDiscount),
        displayTotalDiscount: formatCentsToDisplay(usdTotalDiscount),
        displaySubtotalPriceWithExtra: formatCentsToDisplay(usdSubTotalWithExtraProducts),
        displayTotalPriceWithExtra: formatCentsToDisplay(usdTotalWithExtraProducts),
        displayExtraProductsSubTotalPrice: formatCentsToDisplay(extras.subTotal),
        displayTotalSavingsWithExtraProducts: formatCentsToDisplay(totalSavingsWithExtraProducts),
      };
    }

    /**
     * Calculate the full price breakdown for a given quantity.
     * Orchestrates: discount check → base price → extras → display formatting.
     */
    calculatePrice(qtty, regularPricing) {
      const discount = this.checkDiscount(qtty, regularPricing);
      const base = this.calculateBasePrice(qtty, discount);
      const extras = this.getExtraProductsPrice(qtty);
      const result = this.buildPriceDisplay(base, extras, discount);

      this.log.info('calculatePrice', { qtty, regularPricing, result });
      return result;
    }

    /**
     * Calculate price using a custom per-item price (no discounts applied).
     */
    calculateCustomPrice(qtty, customPrice) {
      const subTotalPrice = customPrice * qtty;
      const totalPrice = Math.round(subTotalPrice);
      const usdTotalDiscount = subTotalPrice - totalPrice;

      const result = {
        usdSubTotal: subTotalPrice,
        usdTotal: totalPrice,
        usdTotalDiscount,
        subTotalPrice,
        totalPrice,
        displaySubtotalPrice: formatCentsToDisplay(subTotalPrice),
        displayTotalPrice: formatCentsToDisplay(totalPrice),
        displaySingleItemPrice: formatCentsToDisplay(totalPrice),
        displayTotalDiscount: formatCentsToDisplay(usdTotalDiscount),
      };

      this.log.info('calculateCustomPrice', { qtty, customPrice, result });
      return result;
    }

    // -----------------------------------------------------------------------
    // Pricing — Multi-product bundles
    // -----------------------------------------------------------------------

    /**
     * Calculate prices for a multi-product bundle by index.
     * Uses each item's variant price and a shared bundleDiscount percentage.
     */
    calculateBundlePrice(bundleIndex) {
      const bundle = this.config.bundles[bundleIndex];
      const discountMultiplier = 1 - (this.config.bundleDiscount / 100);

      let regularTotal = 0;
      let totalQtty = 0;
      bundle.items.forEach((item) => {
        regularTotal += item.price * item.quantity;
        totalQtty += item.quantity;
      });

      const unitCount = bundle.unitCount || totalQtty;
      const discountedTotal = Math.round(regularTotal * discountMultiplier);
      const unitPrice = Math.round(discountedTotal / unitCount);

      return {
        unitPrice,
        totalPrice: discountedTotal,
        regularPrice: regularTotal,
        displayUnitPrice: formatCentsToDisplay(unitPrice),
        displayTotalPrice: formatCentsToDisplay(discountedTotal),
        displayRegularPrice: formatCentsToDisplay(regularTotal),
      };
    }

    /**
     * Render prices for all bundle blocks from config.bundles.
     */
    updateBundlePrices() {
      if (!this.config.bundles) return;
      const symbol = this.resolveCurrencySymbol();

      this.config.bundles.forEach((_bundle, index) => {
        const prices = this.calculateBundlePrice(index);

        qsa('.updatable--bundle-unit-price[data-bundle-index="' + index + '"]').forEach((el) => {
          el.setAttribute('data-currency-symbol', symbol);
          el.classList.add('currency-set');
          el.innerHTML = prices.displayUnitPrice;
        });
        qsa('.updatable--bundle-total-price[data-bundle-index="' + index + '"]').forEach((el) => {
          el.setAttribute('data-currency-symbol', symbol);
          el.classList.add('currency-set');
          el.innerHTML = prices.displayTotalPrice;
        });
        qsa('.updatable--bundle-regular-price[data-bundle-index="' + index + '"]').forEach((el) => {
          el.setAttribute('data-currency-symbol', symbol);
          el.classList.add('currency-set');
          el.innerHTML = prices.displayRegularPrice;
        });
      });
    }

    /**
     * Select a bundle by index: update visual state and store selection.
     */
    selectBundle(index) {
      this.config.selectedBundleIndex = index;

      qsa(this.config.updatableClasses.selectableBundleBlock).forEach((el) => {
        el.classList.toggle('selected', Number(el.dataset.bundleIndex) === index);
      });

      this.log.info('selectBundle', index);
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Selectable blocks & images
    // -----------------------------------------------------------------------

    /**
     * Toggle the 'selected' class on bundle blocks matching the given quantity.
     */
    updateSelectableBlocks(qtty) {
      qsa(this.config.updatableClasses.selectableBundleBlock).forEach((el) => {
        el.classList.remove('selected');
        if (Number(el.dataset.qtty) === qtty) {
          el.classList.add('selected');
        }
      });
    }

    /**
     * Swap product images based on the selected quantity.
     * Expects data-image-src to be a JSON array of image URLs.
     */
    updateQttyImages(qtty) {
      qsa(this.config.updatableClasses.qttyImages).forEach((el) => {
        let imgSrc = el.dataset.imageSrc;

        // jQuery .data() auto-parses JSON; dataset gives a string — parse it
        if (typeof imgSrc === 'string') {
          try {
            imgSrc = JSON.parse(imgSrc);
          } catch (_) {
            return;
          }
        }

        const totalImages = imgSrc.length;
        const imgIndex = qtty - 1;
        const newSrc = qtty >= totalImages ? imgSrc[totalImages - 1] : imgSrc[imgIndex];

        if (newSrc !== el.getAttribute('src')) {
          el.setAttribute('src', newSrc);
        }
      });
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Discount table
    // -----------------------------------------------------------------------

    /**
     * Highlight the active row in the discount table.
     */
    updateDiscountTableSelection(appliedDiscount) {
      qsa(this.config.updatableClasses.discountTable + ' tbody tr').forEach((row) => {
        row.classList.toggle('selected', Number(row.dataset.qtty) === appliedDiscount.qtty);
      });
    }

    /**
     * Populate the discount table with rows from config (run once on init).
     */
    updateDiscountTable() {
      const table = qs(this.config.updatableClasses.discountTable);
      if (!table) return;

      const tbody = qs('tbody', table);
      const discounts = this.config.discounts;

      for (let i = 0; i < discounts.length; i++) {
        const qttyPlus = i + 1 === discounts.length ? '+' : '';
        const d = discounts[i];
        tbody.insertAdjacentHTML('beforeend', `<tr data-qtty="${d.qtty}"><td>${d.qtty}${qttyPlus}</td><td class="discount">${d.percent}% off</td></tr>`);
      }
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Gift blocks
    // -----------------------------------------------------------------------

    /**
     * Toggle the 'selected' class on individual gift blocks.
     */
    markSelectedGifts(gifts, qtty, behavior) {
      gifts.forEach((el) => {
        const rowQtty = Number(el.dataset.qtty);
        if (behavior === 'single') {
          el.classList.toggle('selected', rowQtty === qtty);
        } else {
          el.classList.toggle('selected', rowQtty <= qtty);
        }
      });
    }

    /**
     * Update the savings count and discount percentage shown near gift blocks.
     */
    updateGiftsSummary(discount) {
      const countEl = qs('.savings .count');
      const percentEl = qs('.savings .savings-total');
      const selectedCount = qsa(this.config.updatableClasses.giftBlocks + ' .gift-block.selected').length;

      if (countEl) countEl.innerHTML = selectedCount;
      if (percentEl) percentEl.innerHTML = `${discount.percent}%`;
    }

    /**
     * Update gift block selection state and summary display.
     */
    updateGiftsSelection(qtty, discount) {
      const container = qs(this.config.updatableClasses.giftBlocks);
      if (!container) return;

      const behavior = container.dataset.behavior;
      const gifts = qsa(this.config.updatableClasses.giftBlocks + ' .gift-block');

      this.markSelectedGifts(gifts, qtty, behavior);
      this.updateGiftsSummary(discount);
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Klarna
    // -----------------------------------------------------------------------

    /**
     * Update Klarna on-site messaging placements with the current total price.
     */
    refreshKlarnaPlacements(calculatedPrice) {
      if (!window.KlarnaOnsiteService) return;

      qsa(this.config.updatableClasses.klarnaPlacement).forEach((el) => {
        el.dataset.purchaseAmount = calculatedPrice.displayTotalPriceWithExtra * 100;
      });

      window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
      this.log.info('refreshKlarnaPlacements', calculatedPrice.displayTotalPriceWithExtra);
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Fixed bar
    // -----------------------------------------------------------------------

    /**
     * Show or hide the fixed "Add to Cart" bar depending on whether
     * the main product description section is currently in the viewport.
     */
    toggleFixedBar() {
      const sections = qsa(this.config.observableClasses.mainProductDescription);
      const anyVisible = sections.some((el) => isInViewport(el));

      qsa(this.config.observableClasses.fixedAddToCart).forEach((el) => {
        el.classList.toggle('is-hidden', anyVisible);
      });

      // Adjust launcher and back-top positioning
      const withBarEls = document.querySelectorAll('iframe#launcher, #back-top');
      withBarEls.forEach((el) => {
        el.classList.toggle('withBar', !anyVisible);
      });
    }

    // -----------------------------------------------------------------------
    // DOM Updates — Dynamic currency prices
    // -----------------------------------------------------------------------

    /**
     * Resolve the currency symbol to display from localization settings.
     */
    resolveCurrencySymbol() {
      if (typeof LPLocalizationSettings !== 'undefined' && LPLocalizationSettings.currency?.symbol) {
        return LPLocalizationSettings.currency.symbol;
      }
      return '$';
    }

    /**
     * Render the appropriate price value into a single currency node
     * based on its CSS class modifiers.
     */
    renderCurrencyNode(el, calculatedPrice) {
      if (el.classList.contains('single-item-price')) {
        el.innerHTML = calculatedPrice.displaySingleItemPrice;
      } else if (el.classList.contains('total-discount')) {
        el.innerHTML = calculatedPrice.displayTotalDiscount;
      } else if (el.classList.contains('single-item-discount')) {
        el.innerHTML = calculatedPrice.displaySingleItemDiscount;
      } else if (el.classList.contains('tier-discount-percentage')) {
        el.innerHTML = calculatedPrice.appliedDiscount.percent;
      } else if (el.classList.contains('price-without-discount')) {
        el.innerHTML = calculatedPrice.displaySubtotalPrice;
      } else if (el.classList.contains('total-savings-with-extra-products')) {
        el.innerHTML = calculatedPrice.displayTotalSavingsWithExtraProducts;
      } else if (el.classList.contains('extra-products-subtotal-price')) {
        el.innerHTML = calculatedPrice.displayExtraProductsSubTotalPrice;
      } else if (el.classList.contains('parsable--product-price')) {
        el.innerHTML = parseProductPrice(el.textContent.trim());
      } else {
        el.innerHTML = calculatedPrice.displayTotalPrice;
      }
    }

    /**
     * Iterate all currency-symbol nodes and update their displayed prices.
     * Handles both standard and custom (data-price) pricing.
     */
    updateDynamicPrices() {
      const symbol = this.resolveCurrencySymbol();

      qsa(this.config.updatableClasses.currencySymbol).forEach((el) => {
        const tierQtty = Number(el.dataset.tierQtty);
        const customPrice = el.dataset.price;
        const isRegular = el.classList.contains('regular-price');

        let calculatedPrice;

        // Skip calculatePrice (which needs lpVariant) when a custom price is set
        if (customPrice !== undefined) {
          calculatedPrice = this.calculateCustomPrice(tierQtty, Number(customPrice));
        } else {
          calculatedPrice = this.calculatePrice(tierQtty, isRegular);
        }

        // Set currency symbol attribute
        el.setAttribute('data-currency-symbol', symbol);
        el.classList.add('currency-set');

        if (el.classList.contains('log')) {
          console.log('calculatedPrice', calculatedPrice);
        }

        this.renderCurrencyNode(el, calculatedPrice);
      });
    }

    // -----------------------------------------------------------------------
    // Cart / AJAX — Coupon
    // -----------------------------------------------------------------------

    /**
     * Apply a discount coupon by hitting the /discount endpoint.
     * Returns a Promise so the checkout handler can await it.
     */
    async applyDiscountCoupon(coupon) {
      this.log.info('applyDiscountCoupon', coupon);
      try {
        await fetch(`${window.location.origin}/discount/${encodeURIComponent(coupon)}`);
        this.log.info('Discount applied successfully');
      } catch (err) {
        this.log.error('Error applying discount', err);
      }
    }

    // -----------------------------------------------------------------------
    // Cart / AJAX — Payload helpers
    // -----------------------------------------------------------------------

    /**
     * Build the main cart item from the form (product ID, quantity, free gift properties).
     */
    buildCartPayload(form) {
      let properties = null;
      if (this.config.freeGiftOptions) {
        properties = { '_tmd-fg': JSON.stringify(this.config.freeGiftOptions) };
      }

      // Multi-product bundle path
      if (this.config.bundles && this.config.selectedBundleIndex !== undefined) {
        const bundle = this.config.bundles[this.config.selectedBundleIndex];
        const items = [];

        // Add subscription first so the main product appears above it in cart
        if (bundle.subscription) {
          items.push({
            quantity: 1,
            id: String(bundle.subscription.variantId),
            selling_plan: bundle.subscription.sellingPlanId,
          });
        }

        bundle.items.forEach((item) => {
          items.push({
            quantity: item.quantity,
            id: String(item.id),
            properties,
          });
        });

        return { items };
      }

      // Single-product path (existing behavior)
      const productId = qs('input[name="id"]', form).value;
      const quantity = qs('input[name="quantity"]', form).value;

      return {
        items: [
          {
            quantity: parseInt(quantity),
            id: productId,
            properties,
          },
        ],
      };
    }

    /**
     * Collect checked extra-product items that are not already in the cart.
     * Returns an array of { quantity: 1, id } objects.
     */
    collectExtraProductItems(cart) {
      const items = [];
      const addedIds = [];

      qsa(this.config.observableClasses.extraProducts).forEach((el) => {
        const productId = el.value;
        const isInCart = cart.items.some((item) => item.id === parseInt(productId));

        if (el.checked && !addedIds.includes(productId) && !isInCart) {
          addedIds.push(productId);
          items.push({ quantity: 1, id: productId });
        }
      });

      this.log.info('collectExtraProductItems', items);
      return items;
    }

    // -----------------------------------------------------------------------
    // Cart / AJAX — Submit
    // -----------------------------------------------------------------------

    /**
     * Add items to cart via AJAX. Handles both drawer-cart and standard redirect flows.
     */
    async submitAjaxForm(form, checkoutButton, buyNow, originalButtonContent) {
      const data = this.buildCartPayload(form);

      try {
        // Fetch current cart to check for existing extra products
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Append extra products that aren't already in the cart
        const extraItems = this.collectExtraProductItems(cart);
        data.items.push(...extraItems);

        this.log.info('submitAjaxForm payload', data);

        // Drawer cart path
        if (this.config.useDrawerCart && this.config.drawerCart !== null) {
          await this.config.drawerCart.addToCartMultiple(data, this.config.openDrawerCartOnAdd);
          checkoutButton.innerHTML = originalButtonContent;
          return;
        }

        // Standard path — POST to Shopify cart API
        localStorage.setItem('tmd.openCart', this.config.openDrawerCartOnAdd);

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        if (addResponse.ok) {
          checkoutButton.innerHTML = '<span class="btn-checkout-loader"></span>';
          window.location.href = buyNow ? '/checkout' : '/cart';
        } else {
          this.log.error('Cart add failed', addResponse.status);
          checkoutButton.innerHTML = originalButtonContent;
        }
      } catch (err) {
        this.log.error('submitAjaxForm error', err);
        checkoutButton.innerHTML = originalButtonContent;
      }
    }

    // -----------------------------------------------------------------------
    // Event Handlers — LP:update-qtty sub-helpers
    // -----------------------------------------------------------------------

    /**
     * Update all quantity-related DOM elements (text, fields, humanized words).
     */
    _updateQuantityDisplay(qtty) {
      // Numeric quantity
      qsa(this.config.updatableClasses.quantity).forEach((el) => {
        el.innerHTML = qtty;
      });

      // Input fields
      qsa(this.config.updatableClasses.quantityField).forEach((el) => {
        el.value = qtty;
      });

      // Humanized quantity words (e.g. "two units")
      qsa(this.config.updatableClasses.quantityWords).forEach((el) => {
        const text = humanize(qtty) + (qtty > 1 ? ' units' : ' unit');
        el.innerHTML = el.classList.contains('no-brackets') ? text : `(${text})`;
      });
    }

    /**
     * Update all price-related DOM elements with the calculated values.
     */
    _updatePriceDisplay(calculatedPrice) {
      const cls = this.config.updatableClasses;

      qsa(cls.discountPercent).forEach((el) => {
        el.innerHTML = calculatedPrice.appliedDiscount.percent;
      });
      qsa(cls.singleItemPrice).forEach((el) => {
        el.innerHTML = calculatedPrice.displaySingleItemPrice;
      });
      qsa(cls.subtotalPrice).forEach((el) => {
        el.innerHTML = calculatedPrice.displaySubtotalPrice;
      });
      qsa(cls.totalPrice).forEach((el) => {
        el.innerHTML = calculatedPrice.displayTotalPrice;
      });
      qsa(cls.subtotalPriceWithExtra).forEach((el) => {
        el.innerHTML = calculatedPrice.displaySubtotalPriceWithExtra;
      });
      qsa(cls.totalPriceWithExtra).forEach((el) => {
        el.innerHTML = calculatedPrice.displayTotalPriceWithExtra;
      });
      qsa(cls.totalSavingsWithExtraProducts).forEach((el) => {
        el.innerHTML = calculatedPrice.displayTotalSavingsWithExtraProducts;
      });
      qsa(cls.extraProductsSubTotalPrice).forEach((el) => {
        el.innerHTML = calculatedPrice.displayExtraProductsSubTotalPrice;
      });

      // Total discount row — show/hide
      qsa(cls.totalDiscount).forEach((el) => {
        if (calculatedPrice.usdTotalDiscount > 0) {
          const valueEl = qs('.value', el);
          if (valueEl) valueEl.innerHTML = calculatedPrice.displayTotalDiscount;
          el.classList.remove('hide');
        } else {
          el.classList.add('hide');
        }
      });
    }

    /**
     * Enable/disable the quantity +/- buttons based on bounds.
     */
    _updateQuantityButtons(qtty) {
      qsa(this.config.updatableClasses.minusButton).forEach((el) => {
        el.classList.toggle('disabled', qtty <= 1);
      });
      qsa(this.config.updatableClasses.plusButton).forEach((el) => {
        el.classList.toggle('disabled', qtty >= 99);
      });
    }

    // -----------------------------------------------------------------------
    // Event Handlers — Custom events
    // -----------------------------------------------------------------------

    /**
     * Handle the LP:update-qtty event: recalculate prices and update all DOM.
     */
    _onUpdateQtty(event, qtty) {
      window.selectedQtty = qtty;
      const calculatedPrice = this.calculatePrice(qtty, false);

      this.log.info('LP:update-qtty', { qtty, calculatedPrice });

      this._updateQuantityDisplay(qtty);
      this._updatePriceDisplay(calculatedPrice);
      this._updateQuantityButtons(qtty);

      this.updateSelectableBlocks(qtty);
      this.updateQttyImages(qtty);
      this.updateDiscountTableSelection(calculatedPrice.appliedDiscount);
      this.updateGiftsSelection(qtty, calculatedPrice.appliedDiscount);
      this.refreshKlarnaPlacements(calculatedPrice);

      // Notify about discount state
      trigger('LP:discount-applied', calculatedPrice.totalPrice !== calculatedPrice.subTotalPrice);
    }

    /**
     * Handle the LP:discount-applied event: toggle visibility of discount indicators.
     */
    _onDiscountApplied(event, hasDiscount) {
      qsa(this.config.updatableClasses.hasDiscount).forEach((el) => {
        if (hasDiscount) {
          el.style.visibility = 'visible';
          if (el.classList.contains('no-display')) {
            el.style.display = 'inline';
          }
        } else {
          el.style.visibility = 'hidden';
          if (el.classList.contains('no-display')) {
            el.style.display = 'none';
          }
        }
      });
    }

    /**
     * Handle the LP:global-e-update event: update currency symbol from Global-e.
     */
    _onGlobalEUpdate(event, localizedConfiguration) {
      if (this.config.geSettings.useGlobaleCurrency) {
        qsa(this.config.updatableClasses.currencySymbol).forEach((el) => {
          el.setAttribute('data-currency-symbol', localizedConfiguration.UserCurrency.Symbol);
          el.classList.add('currency-set');
        });
      }

      // Refresh bundle prices with updated currency
      this.updateBundlePrices();
    }

    // -----------------------------------------------------------------------
    // UI Bindings — Click / change handlers
    // -----------------------------------------------------------------------

    /**
     * Handle checkout button click: apply coupon (if any) then submit form.
     */
    async _handleCheckoutClick(e) {
      e.preventDefault();

      const form = qs(this.config.observableClasses.addToCartForm);
      const btn = e.currentTarget;
      const originalContent = btn.innerHTML;
      const isBuyNow = form.dataset.action === 'buy-now';

      // Lock button size and show loading state
      btn.style.width = btn.offsetWidth + 'px';
      btn.style.height = btn.offsetHeight + 'px';
      btn.innerHTML = 'Adding...';

      this.log.info('Checkout click', { isBuyNow, coupon: btn.dataset.coupon });

      // Apply coupon if specified
      const coupon = btn.dataset.coupon;
      if (coupon) {
        await this.applyDiscountCoupon(coupon);
      }

      this.submitAjaxForm(form, btn, isBuyNow, originalContent);
    }

    /**
     * Handle quantity +/- button clicks.
     */
    _handleQttyChange(e) {
      const el = e.currentTarget;
      const newQtty = el.classList.contains('plus') ? window.selectedQtty + 1 : window.selectedQtty - 1;

      if (newQtty > 0 && newQtty < 100) {
        trigger('LP:update-qtty', newQtty);
      }
    }

    /**
     * Update delivery box visibility based on the selected radio button.
     */
    _updateDeliveryBoxVisibility(radio) {
      const imageBlock = radio.closest('.image-block');
      if (imageBlock && (imageBlock.classList.contains('two-unit') || imageBlock.classList.contains('three-unit'))) {
        qsa('.freeGifts-message').forEach((el) => (el.style.display = 'none'));
        const targetClass = radio.value;
        qsa('.delivery-box-inner').forEach((el) => {
          el.style.display = el.classList.contains(targetClass) ? '' : 'none';
        });
      } else {
        qsa('.delivery-box-inner').forEach((el) => (el.style.display = 'none'));
        qsa('.freeGifts-message').forEach((el) => (el.style.display = ''));
      }
    }

    /**
     * Handle bundle block click: update quantity, selection, and delivery box.
     */
    _handleBundleClick(e) {
      const clicked = e.currentTarget;

      // Multi-product bundle path (data-bundle-index)
      if (clicked.dataset.bundleIndex !== undefined) {
        this.selectBundle(Number(clicked.dataset.bundleIndex));
        return;
      }

      // Single-product quantity path (data-qtty) — existing behavior
      const bundleQtty = Number(clicked.dataset.qtty);
      trigger('LP:update-qtty', bundleQtty);

      // Handle bundle-specific UI updates (radio buttons, delivery boxes)
      if (clicked.classList.contains('bundle-update')) {
        qsa(this.config.updatableClasses.selectableBundleBlock).forEach((el) => el.classList.remove('selected'));

        const radio = qs('input[type="radio"]', clicked);
        if (radio) {
          radio.checked = true;
          this._updateDeliveryBoxVisibility(radio);
        }

        clicked.classList.add('selected');
      }
    }

    /**
     * Sync all extra-product checkboxes sharing the same product ID.
     */
    _syncExtraProductCheckboxes(productId, isChecked) {
      qsa(this.config.observableClasses.extraProducts).forEach((el) => {
        if (el.dataset.productId === productId) {
          el.checked = isChecked;
        }
      });
    }

    /**
     * Handle extra-product checkbox change: sync siblings and recalculate.
     */
    _handleExtraProductChange(e) {
      const el = e.currentTarget;
      const productId = el.dataset.productId;
      const isChecked = el.checked;

      this._syncExtraProductCheckboxes(productId, isChecked);

      // Recalculate with current quantity
      const selectedQtty = qs(this.config.updatableClasses.quantityField)?.value;
      if (selectedQtty) {
        trigger('LP:update-qtty', parseInt(selectedQtty));
      }
    }

    // -----------------------------------------------------------------------
    // Initialization
    // -----------------------------------------------------------------------

    /**
     * Execute a callback when the DOM is ready, or immediately if it already is.
     */
    _onReady(fn) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn);
      } else {
        fn();
      }
    }

    /**
     * Register all document-level custom event listeners.
     */
    _bindCustomEvents() {
      listen('LP:update-qtty', (event, qtty) => this._onUpdateQtty(event, qtty));
      listen('LP:discount-applied', (event, hasDiscount) => this._onDiscountApplied(event, hasDiscount));
      listen('LP:global-e-update', (event, config) => this._onGlobalEUpdate(event, config));

      // Fixed bar toggling on scroll/resize with rAF throttle to prevent jank
      let ticking = false;
      const onScrollResize = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            this.toggleFixedBar();
            ticking = false;
          });
          ticking = true;
        }
      };
      document.addEventListener('scroll', onScrollResize);
      window.addEventListener('resize', onScrollResize);
    }

    /**
     * Run startup tasks: update prices, build discount table, trigger initial quantity.
     */
    _initLoadEvents() {
      this.updateDynamicPrices();
      this.updateDiscountTable();
      trigger('LP:update-qtty', window.selectedQtty);

      // Set up drawer cart element if configured
      const existingCart = document.querySelector('tmd-cart-drawer') || false;
      if (this.config.useDrawerCart && existingCart === false) {
        this.config.drawerCart = document.createElement('tmd-cart-drawer');
        this.config.drawerCart.classList.add('default');
        document.body.prepend(this.config.drawerCart);
      } else {
        this.config.drawerCart = existingCart;
      }

      // Multi-product bundle initialization
      if (this.config.bundles) {
        this.updateBundlePrices();
        this.selectBundle(this.config.selectedBundleIndex || 0);
      }

      this.log.info('initLoadEvents complete');
    }

    /**
     * Bind all UI event listeners and run initial load tasks.
     * Called once on DOMContentLoaded.
     */
    _init() {
      this.toggleFixedBar();

      // Checkout buttons
      qsa(this.config.updatableClasses.checkoutButton).forEach((el) => {
        if (this.config.couponCode) {
          el.dataset.coupon = this.config.couponCode;
        }
        el.addEventListener('click', (e) => this._handleCheckoutClick(e));
      });

      // Quantity +/- buttons
      qsa(this.config.observableClasses.qttyChangeButton).forEach((el) => {
        el.addEventListener('click', (e) => this._handleQttyChange(e));
      });

      // Selectable bundle blocks
      qsa(this.config.updatableClasses.selectableBundleBlock).forEach((el) => {
        el.addEventListener('click', (e) => this._handleBundleClick(e));
      });

      // Extra product checkboxes
      qsa(this.config.observableClasses.extraProducts).forEach((el) => {
        el.addEventListener('change', (e) => this._handleExtraProductChange(e));
      });

      // Run startup tasks
      this._initLoadEvents();

      // Reveal Klarna container
      qsa(this.config.updatableClasses.klarnaContainer).forEach((el) => {
        el.classList.remove('globale-hide');
      });

      this.log.info('LPCustomHandlers initialized');
    }
  }

  // ---------------------------------------------------------------------------
  // Auto-instantiation — drop-in: just include this script in <head>
  // ---------------------------------------------------------------------------

  // Expose the class for manual instantiation if needed
  window.LPCustomHandlers = LPCustomHandlers;

  document.addEventListener('DOMContentLoaded', () => {
    // typeof check works with both var and let/const declarations
    const baseConfig = typeof LPBaseConfig !== 'undefined' ? LPBaseConfig : {};
    window.LP = new LPCustomHandlers(baseConfig);
  });
})();
